/* eslint-disable ember/no-computed-properties-in-native-classes */
import { not, and, notEmpty } from '@ember/object/computed';
import { service } from '@ember/service';
import Model, { attr, belongsTo, type AsyncBelongsTo } from '@ember-data/model';
import { tracked } from '@glimmer/tracking';
import type LocationModel from 'garaje/models/location';
import type VisitorEntranceModel from 'garaje/models/visitor-entrance';
import type DeviceStatusService from 'garaje/services/device-status-service';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type LocationFeatureFlagsService from 'garaje/services/location-feature-flags';
import deviceStatus from 'garaje/utils/decorators/device-status';
import { isIpadDeviceType, isListableIpadStatus, isSimulatorDeviceType } from 'garaje/utils/device-type';

import type AbstractPrinter from './abstract/printer';
import type ZoneModel from './zone';

@deviceStatus
class DeviceModel extends Model {
  @service declare locationFeatureFlags: LocationFeatureFlagsService;
  // Company-trafficked flags. Connect/property iPads (zone-scoped) gate onto live
  // DeviceStatusService telemetry via property-devices-service-status-api — see the
  // @deviceStatus decorator's isDeviceStatusServiceEnabled.
  @service declare featureFlags: FeatureFlagsService;
  @service('device-status-service') declare deviceStatusService: DeviceStatusService;
  @belongsTo('location', { async: true, inverse: 'devices' }) declare location: AsyncBelongsTo<LocationModel>;
  @belongsTo('abstract/printer', { async: true, inverse: 'devices', polymorphic: true }) declare printer:
    | AsyncBelongsTo<AbstractPrinter>
    | AbstractPrinter;
  @belongsTo('abstract/printer', { async: true, inverse: null, polymorphic: true }) declare failoverPrinter:
    | AsyncBelongsTo<AbstractPrinter>
    | AbstractPrinter
    | null;
  @belongsTo('visitor-entrance', { async: true, inverse: 'devices' })
  declare visitorEntrance: AsyncBelongsTo<VisitorEntranceModel>;
  @belongsTo('zone', { async: true, inverse: 'devices' }) declare zone: AsyncBelongsTo<ZoneModel>;

  @attr('string') declare userToken: string;
  @attr('boolean') declare isConnected: boolean;
  @attr('date') declare grantExpiresAt: Date;
  @attr('string') declare osVersion: string;
  @attr('string') declare maximumOsVersion: string;
  @attr('string') declare appVersion: string | null;
  @attr('string') declare currentIpadAppVersion: string;
  @attr('string') declare ipAddress: string;
  @attr('string') declare wifiNetwork: string;
  @attr('string') declare printerStatus: string;
  @attr('string') declare failoverPrinterStatus: string;
  @attr('date') declare lastHeartbeatAt: Date;
  @attr('boolean') declare isSleeping: boolean;
  @attr('string') declare deviceType: string;
  @attr('string') declare name: string;
  @attr('string') declare alias: string | null;

  @tracked newPingRequestWentOutAt: Date | null = null;
  @tracked oldPingRequestWentOutAt: Date | null = null;
  @not('isDeleted') declare isNotDeleted: boolean;
  @and('isConnected', 'isNotDeleted') declare isVisible: boolean;
  @notEmpty('deviceType') declare hasType: boolean;
  // Provided by the @deviceStatus decorator (app/utils/decorators/device-status.js).
  // True iff `lastHeartbeatAt` is within IPAD_LAST_SEEN_THRESHOLD (or the
  // DeviceStatusService reports CONNECTED when the `devices-service-status-api`
  // flag is on). Declared here so callers don't need local intersection casts.
  declare isIpadSeenRecently: boolean;
  // Provided by the @deviceStatus decorator. True when this device's connection
  // state is sourced from live DeviceStatusService telemetry rather than the
  // legacy heartbeat threshold.
  declare isDeviceStatusServiceEnabled: boolean;

  get displayName(): string {
    if (this.alias) {
      return `${this.alias} (${this.name})`;
    }
    return this.name;
  }

  get isIpad(): boolean {
    return isIpadDeviceType(this.deviceType);
  }

  get isSimulator(): boolean {
    return isSimulatorDeviceType(this.deviceType);
  }

  /**
   * Whether this device belongs in an iPad list/count. Mirrors the per-location
   * iPads page (visitors/devices/ipads/index/controller.ts): when devices-service
   * telemetry is live, ground the check in that telemetry (isListableIpadStatus)
   * rather than the store record — a ghost enrollment stub can wear real-looking
   * envoy-web attributes (see device-type.ts). When the flag is off, fall back to
   * the legacy record-based rule.
   *
   * A device with no status is only excluded once its scope's snapshot has
   * actually loaded (then it's genuinely absent from devices-service). Before the
   * snapshot lands — activateScope is fire-and-forget, so printers pages can
   * render first — use the legacy record check, or every printer would
   * transiently read "Not In Use" from an empty listable set. Both getStatus and
   * loadedScopeKeys are tracked, so this recomputes when the snapshot arrives.
   */
  get isListableIpad(): boolean {
    const showSimulators = this.locationFeatureFlags.isEnabled('visitors-devices-dashboard-show-simulators');

    if (this.isDeviceStatusServiceEnabled) {
      const status = this.deviceStatusService.getStatus(this.id);
      if (status) {
        return isListableIpadStatus(status, { showSimulators });
      }
      if (this.deviceStatusService.loadedScopeKeys.has(this.statusScopeKey)) {
        return false;
      }
    }

    return this.isIpad && (!this.isSimulator || showSimulators);
  }

  /**
   * The printer connection status to trust, preferring live devices-service
   * telemetry over the `printerStatus` attribute (SQ-7730).
   *
   * `devices.printer_status` is written only by the iPad's JSON:API device PATCH
   * (DeviceResource.updatable_fields). envoy-ipad sends that PATCH from two places:
   * periodically inside the HTTP heartbeat, and edge-triggered whenever printer status
   * changes. Only the periodic one is gated by `kiosk-http-heartbeat-disabled` — and
   * that flag is on everywhere, so fleet-wide this column has only an edge-triggered
   * writer: a printer that hasn't changed state never re-asserts its status and the
   * column drifts blank or stale. (`printers.status` is unaffected — DevicePingUpdater
   * still writes it via the Kafka printer-change path.)
   *
   * Measured in production: the column is blank for every device at the reporting
   * locations, and for 52% of bluetooth / 19% of ip iPad-paired devices fleet-wide,
   * while devices-service telemetry reports `printer-status` connected for the same
   * devices. The blankness is measured; the drift mechanism above is inferred.
   *
   * Telemetry is the reliably fresh source — it is the same JSON the heartbeat posts,
   * rewritten on every publish — so read through to it the way `isIpadSeenRecently`
   * already does for connection status. Falls back to the record when the flag is off,
   * the telemetry key is absent, or the snapshot hasn't landed, so flag-off and
   * pre-snapshot behavior are unchanged.
   *
   * `failoverPrinterStatus` deliberately keeps reading envoy-web and has no
   * `liveFailoverPrinterStatus` counterpart. The iPad's telemetry payload has no
   * failover field (0 of 32,412 blobs carry one), and the PATCH that does carry it is
   * not heartbeat-gated, so envoy-web remains its live source.
   */
  get livePrinterStatus(): string {
    if (this.isDeviceStatusServiceEnabled) {
      const fromTelemetry = this.deviceStatusService.getStatus(this.id)?.telemetry?.['printer-status'];
      if (typeof fromTelemetry === 'string' && fromTelemetry) {
        return fromTelemetry;
      }
    }

    return this.printerStatus;
  }

  // Mirrors the @deviceStatus decorator's scope inference: Connect/property iPads
  // are zone-scoped (their location_id is null), visitor iPads location-scoped.
  private get statusScopeKey(): string {
    // @ts-expect-error - belongsTo method accepts string for relationship name
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const zoneId = this.belongsTo('zone').id() as string | null;
    // @ts-expect-error - belongsTo method accepts string for relationship name
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const locationId = this.belongsTo('location').id() as string | null;
    if (zoneId && !locationId) {
      return `zone:${zoneId}`;
    }
    return `location:${locationId}`;
  }

  get status(): string {
    if (!this.isConnected) return 'Disconnected';

    return this.isSleeping ? 'Sleeping' : 'Connected';
  }

  get locationName(): string {
    let returnValue = '';
    try {
      // @ts-expect-error - belongsTo method accepts string for relationship name
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const location = this.belongsTo('location').value() as LocationModel | null;
      if (location?.name) {
        returnValue = location.name;
      }
    } catch (_e) {
      // Location not loaded, leave as empty string
    }
    return returnValue;
  }

  setPingTime(): void {
    if (this.newPingRequestWentOutAt) {
      this.oldPingRequestWentOutAt = this.newPingRequestWentOutAt;
    }
    this.newPingRequestWentOutAt = new Date();
  }

  toDataForCsvExport(): Record<string, string> {
    return {
      Name: this.displayName || '',
      'IP Address': this.ipAddress || '',
      Location: this.locationName,
      Status: this.status,
      'Last Checked In': this.lastHeartbeatAt?.toISOString() || '',
      'App Version': this.appVersion || '',
    };
  }
}

export default DeviceModel;

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    device: DeviceModel;
  }
}
